/** Contractor agreement generator (Tom 2026-09-04: "a template generator for
 * this contract … put the info that i need and it will put it where it needs
 * to"). The backend fills Tom's own Word template (every clause, font and
 * table untouched) and streams back a .docx:
 *   GET  /api/contracts/contractor-agreement/defaults → { defaults, presets }
 *   PUT  /api/contracts/contractor-agreement/defaults   (admin)
 *   POST /api/contracts/contractor-agreement/preview → { tokens, filename }
 *   POST /api/contracts/contractor-agreement          → the file
 *   POST /api/contracts/w9-parse (multipart)          → contractor facts
 * Split math lives here too (mirrors the backend) so the modal previews the
 * installment sentence instantly while typing. */
import { API_BASE } from "../../config";
import { BackendError, backendFetch } from "../../lib/backend";

export interface AgreementValues {
  effectiveDate?: string;     // ISO yyyy-mm-dd (input type=date)
  ownerName?: string;
  ownerAddress?: string;
  contractorName?: string;
  contractorAddress?: string;
  projectAddress?: string;
  price?: string;             // "52,761.42" | "$52761.42"
  split?: number[];           // percentages, e.g. [20,20,20,20,20]
  startDate?: string;
  completionDate?: string;
  ldPerDay?: string;
  startGraceDays?: string;
  lender?: string;
  /** legacy — the v2 template (2026-10-30) names no venue county; still saved with the defaults */
  venueCounty?: string;
  /** Exhibit B — the Scope of Work's date and page count (v2 template) */
  sowDate?: string;
  sowPages?: string;
}

export interface AgreementDefaults {
  ownerName: string;
  ownerAddress: string;
  ldPerDay: string;
  startGraceDays: string;
  lender: string;
  venueCounty: string;
  split: number[];
}

export const BUILTIN_DEFAULTS: AgreementDefaults = {
  ownerName: "ETO Property Group LLC",
  ownerAddress: "689 Brechin Dr, Winter Park, FL 32792",
  ldPerDay: "400",
  startGraceDays: "3",
  lender: "Kiavi",
  venueCounty: "Orange",
  split: [20, 20, 20, 20, 20],
};

/** The quick picks Tom named — "20 20 20 20 20, or 25 25 25 25, or 33 33 33". */
export const SPLIT_PRESETS: { id: string; label: string; split: number[] }[] = [
  { id: "5x20", label: "5 × 20%", split: [20, 20, 20, 20, 20] },
  { id: "4x25", label: "4 × 25%", split: [25, 25, 25, 25] },
  { id: "3x33", label: "3 × 33%", split: [33, 33, 34] },
  { id: "2x50", label: "2 × 50%", split: [50, 50] },
];

// ---- split math (same rules as the backend) ---------------------------------

/** "20/20/20/20/20" | "25,25,25,25" | "33 33 33" → [20,…]. The last number
 * absorbs any rounding slack so the list always sums to 100 (33/33/33 →
 * 33/33/34). Returns { error } when the numbers don't add up. */
export function parseSplit(text: string): { split: number[] } | { error: string } {
  const parts = text.trim().split(/[\s,/|;]+/).filter(Boolean);
  if (!parts.length) return { error: "Type the payment percentages, e.g. 20/20/20/20/20" };
  const nums: number[] = [];
  for (const p of parts) {
    const n = Math.round(Number(p.replace("%", "")));
    if (!Number.isFinite(n) || n <= 0 || n > 100) return { error: `"${p}" isn't a percentage between 1 and 100` };
    nums.push(n);
  }
  if (nums.length > 12) return { error: "At most 12 payments" };
  const total = nums.reduce((a, b) => a + b, 0);
  if (Math.abs(total - 100) > 3) return { error: `Adds up to ${total}%, not 100%` };
  const out = [...nums];
  out[out.length - 1] = out[out.length - 1]! + (100 - total);
  if (out[out.length - 1]! <= 0) return { error: "Adds up to more than 100%" };
  return { split: out };
}

export function parseMoney(v: string | number | undefined): number {
  if (typeof v === "number") return Number.isFinite(v) ? Math.round(v * 100) / 100 : 0;
  const s = String(v ?? "").replace(/[^0-9.-]/g, "");
  const n = Number(s);
  return s && Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
}

export const fmtMoney = (n: number): string =>
  (n < 0 ? "-$" : "$") + Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Per-installment dollars in cents-exact arithmetic; the last one takes the
 * rounding remainder so they sum to the price to the cent. */
export function splitAmounts(price: number, split: number[]): number[] {
  const cents = Math.round(price * 100);
  const out: number[] = [];
  let used = 0;
  for (let i = 0; i < split.length - 1; i++) {
    const c = Math.round((cents * split[i]!) / 100);
    out.push(c / 100);
    used += c;
  }
  out.push((cents - used) / 100);
  return out;
}

/** The sentence that lands in §3: "20% ($10,552.28) at 20% completion, …, and
 * 20% ($10,552.30) at full completion". */
export function splitSentence(price: number, split: number[]): string {
  const amts = splitAmounts(price, split);
  let cum = 0;
  const parts = split.map((pct, i) => {
    cum += pct;
    const last = i === split.length - 1;
    return `${pct}% (${fmtMoney(amts[i]!)}) ${last ? "at full completion" : `at ${cum}% completion`}`;
  });
  if (parts.length === 1) return parts[0]!;
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
}

const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve",
  "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
function below1000(n: number): string {
  const out: string[] = [];
  if (n >= 100) { out.push(ONES[Math.floor(n / 100)] + " Hundred"); n %= 100; }
  if (n >= 20) out.push(TENS[Math.floor(n / 10)] + (n % 10 ? "-" + ONES[n % 10] : ""));
  else if (n) out.push(ONES[n]!);
  return out.join(" ");
}
export function intToWords(n: number): string {
  if (n === 0) return "Zero";
  const parts: string[] = [];
  for (const [val, name] of [[1e9, "Billion"], [1e6, "Million"], [1e3, "Thousand"]] as [number, string][]) {
    if (n >= val) { parts.push(below1000(Math.floor(n / val)) + " " + name); n %= val; }
  }
  if (n) parts.push(below1000(n));
  return parts.join(" ");
}
/** "$52,761.42" → "Fifty-Two Thousand Seven Hundred Sixty-One and 42/100 U.S. Dollars" */
export function moneyWords(price: number): string {
  const cents = Math.round(price * 100);
  const d = Math.floor(cents / 100);
  const c = cents - d * 100;
  return `${intToWords(d)} and ${String(c).padStart(2, "0")}/100 U.S. Dollars`;
}

// ---- backend calls -----------------------------------------------------------

export async function getAgreementDefaults(token: string | null): Promise<AgreementDefaults> {
  const j = await backendFetch<{ defaults?: Partial<AgreementDefaults> }>(token, "/api/contracts/contractor-agreement/defaults", { timeoutMs: 20000 });
  const d = j?.defaults ?? {};
  const split = Array.isArray(d.split) && d.split.length ? d.split.map(Number).filter((n) => Number.isFinite(n)) : BUILTIN_DEFAULTS.split;
  return {
    ownerName: String(d.ownerName || BUILTIN_DEFAULTS.ownerName),
    ownerAddress: String(d.ownerAddress || BUILTIN_DEFAULTS.ownerAddress),
    ldPerDay: String(d.ldPerDay || BUILTIN_DEFAULTS.ldPerDay),
    startGraceDays: String(d.startGraceDays || BUILTIN_DEFAULTS.startGraceDays),
    lender: String(d.lender || BUILTIN_DEFAULTS.lender),
    venueCounty: String(d.venueCounty || BUILTIN_DEFAULTS.venueCounty),
    split,
  };
}

export async function saveAgreementDefaults(token: string | null, d: AgreementDefaults): Promise<void> {
  await backendFetch(token, "/api/contracts/contractor-agreement/defaults", { method: "PUT", body: { defaults: d }, timeoutMs: 20000 });
}

/** The exact strings the document will carry (server-side truth). */
export async function previewAgreement(token: string | null, values: AgreementValues): Promise<{ tokens: Record<string, string>; filename: string }> {
  return backendFetch(token, "/api/contracts/contractor-agreement/preview", { method: "POST", body: { values }, timeoutMs: 20000 });
}

async function rawError(res: Response): Promise<BackendError> {
  const text = await res.text();
  let detail = String(res.status);
  try {
    const j = JSON.parse(text);
    if (typeof j?.detail === "string") detail = j.detail;
  } catch { /* keep status */ }
  return new BackendError(res.status, detail.slice(0, 200));
}

/** "Contractor Agreement - <contractor> - <street>.docx" (same rule as the
 * backend's suggested_filename; computed here because a custom response
 * header isn't readable cross-origin). */
export function suggestedFilename(values: AgreementValues): string {
  const clean = (s: string) => s.replace(/[^A-Za-z0-9]+/g, " ").trim();
  const who = clean(values.contractorName || "Contractor");
  const where = clean((values.projectAddress || "").split(",")[0] ?? "");
  return `Contractor Agreement - ${who}${where ? " - " + where : ""}.docx`;
}

/** Generate the .docx — raw fetch, the response is a file, not JSON. */
export async function generateAgreement(token: string | null, values: AgreementValues, filename?: string): Promise<{ blob: Blob; filename: string }> {
  const name = filename || suggestedFilename(values);
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), 90000);
  try {
    const res = await fetch(API_BASE + "/api/contracts/contractor-agreement", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ values, filename: name }),
      signal: ctl.signal,
    });
    if (!res.ok) throw await rawError(res);
    return { blob: await res.blob(), filename: res.headers.get("X-Doc-Filename") || name };
  } finally {
    clearTimeout(t);
  }
}

export interface W9Info {
  name: string;
  businessName: string;
  address: string;
  cityStateZip: string;
  contractorName: string;
  contractorAddress: string;
  how: string;
}

/** Upload a contractor's W-9 PDF; back come the name + address for the
 * agreement (never the TIN). Multipart → raw fetch, browser sets the boundary. */
export async function parseW9(token: string | null, file: File): Promise<W9Info> {
  const fd = new FormData();
  fd.append("file", file, file.name || "w9.pdf");
  if (token) fd.append("token", token);
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), 90000);
  try {
    const res = await fetch(API_BASE + "/api/contracts/w9-parse", {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
      signal: ctl.signal,
    });
    if (!res.ok) throw await rawError(res);
    return (await res.json()) as W9Info;
  } finally {
    clearTimeout(t);
  }
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

/** "Jul 24, 26" | "07/24/2026" | "2026-07-24" (deal date fields) → ISO for <input type=date>; "" if unknown. */
export function toIsoDate(v: string | null | undefined): string {
  const s = String(v ?? "").trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.exec(s);
  if (m) {
    const y = m[3]!.length === 2 ? "20" + m[3] : m[3];
    return `${y}-${m[1]!.padStart(2, "0")}-${m[2]!.padStart(2, "0")}`;
  }
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }
  return "";
}

// ---- Partnership MOU (Tom 2026-09-10) ------------------------------------------
// "the same way you made contractor template ... in deals under purchasing at
// the bottom. the things that should be changable are the llcs, addresses,
// parcel, ein and names".

export interface MouValues {
  partnerPeople?: string[];   // one or two people
  partnerLlc?: string;
  partnerEin?: string;
  tomName?: string;
  tomLlc?: string;
  tomEin?: string;
  propertyAddress?: string;
  legalDescription?: string;  // parcel / legal description
  lender?: string;
  tomPct?: string;
  partnerPct?: string;
}

export interface MouDefaults { tomName: string; tomLlc: string; tomEin: string; lender: string; tomPct: string; partnerPct: string }
export interface MouPartner { label: string; people: string[]; llc: string; ein: string }

export const MOU_BUILTIN: MouDefaults = { tomName: "Tom Zadok", tomLlc: "ETO Property Group LLC", tomEin: "39-3327691", lender: "Kiavi", tomPct: "50", partnerPct: "50" };

/** Profit-split presets (Tom 2026-09-24: "60%/40%, 55%/45% and 50%/50% —
 * the 60 40 and 55 45 could be to either side, i want the option to
 * choose"). One button per choice, both directions spelled out, so the
 * side that gets the bigger share is never a guess. The template prints
 * "{TOM_PCT}% shall go to Tom / {PARTNER_PCT}% shall go to <partner>", so
 * any pair adding to 100 renders correctly. */
export interface ProfitSplit { tom: number; partner: number }
export const PROFIT_SPLITS: ProfitSplit[] = [
  { tom: 50, partner: 50 },
  { tom: 60, partner: 40 }, { tom: 40, partner: 60 },
  { tom: 55, partner: 45 }, { tom: 45, partner: 55 },
];
/** "60% you / 40% partner" — `partnerShort` is the partner's first name(s)
 * once known, so the buttons read "60% you / 40% Omer & Eric". */
export function profitSplitLabel(s: ProfitSplit, partnerShort?: string): string {
  const who = partnerShort?.trim() || "partner";
  return s.tom === s.partner ? `${s.tom}% / ${s.partner}%` : `${s.tom}% you / ${s.partner}% ${who}`;
}
/** Which preset (if any) a tomPct/partnerPct pair is. */
export function matchProfitSplit(tomPct: string | undefined, partnerPct: string | undefined): ProfitSplit | null {
  const t = Number(tomPct), p = Number(partnerPct);
  return PROFIT_SPLITS.find((s) => s.tom === t && s.partner === p) ?? null;
}

/** "Omer Yacobovitch & Eric Rahamim" / "A and B" / "A, B" → up to two names. */
export function parsePeople(text: string): string[] {
  return text.split(/\s*(?:&|\band\b|,|\/)\s*/i).map((s) => s.trim()).filter(Boolean).slice(0, 3);
}
export const firstNames = (people: string[]): string => people.map((p) => p.split(/\s+/)[0] ?? "").filter(Boolean).join(" & ");
export function fmtEin(v: string): string {
  const d = v.replace(/\D/g, "");
  return d.length === 9 ? `${d.slice(0, 2)}-${d.slice(2)}` : v;
}

export async function getMouDefaults(token: string | null): Promise<{ defaults: MouDefaults; partners: MouPartner[] }> {
  const j = await backendFetch<{ defaults?: Partial<MouDefaults>; partners?: MouPartner[] }>(token, "/api/contracts/partnership-mou/defaults", { timeoutMs: 20000 });
  const d = j?.defaults ?? {};
  return {
    defaults: {
      tomName: String(d.tomName || MOU_BUILTIN.tomName), tomLlc: String(d.tomLlc || MOU_BUILTIN.tomLlc), tomEin: String(d.tomEin || MOU_BUILTIN.tomEin),
      lender: String(d.lender || MOU_BUILTIN.lender), tomPct: String(d.tomPct || MOU_BUILTIN.tomPct), partnerPct: String(d.partnerPct || MOU_BUILTIN.partnerPct),
    },
    partners: Array.isArray(j?.partners) ? j!.partners!.filter((p) => p && Array.isArray(p.people) && p.people.length) : [],
  };
}

export async function saveMouDefaults(token: string | null, body: { defaults?: MouDefaults; partners?: MouPartner[] }): Promise<void> {
  await backendFetch(token, "/api/contracts/partnership-mou/defaults", { method: "PUT", body, timeoutMs: 20000 });
}

export function mouFilename(values: MouValues): string {
  const who = firstNames(values.partnerPeople ?? []).replace(/[^A-Za-z0-9& ]+/g, " ").trim() || "Partner";
  const where = (values.propertyAddress || "").split(",")[0]!.replace(/[^A-Za-z0-9]+/g, " ").trim();
  return `Partnership MOU - ${who}${where ? " - " + where : ""}.docx`;
}

export async function generateMou(token: string | null, values: MouValues): Promise<{ blob: Blob; filename: string }> {
  const name = mouFilename(values);
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), 90000);
  try {
    const res = await fetch(API_BASE + "/api/contracts/partnership-mou", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ values, filename: name }),
      signal: ctl.signal,
    });
    if (!res.ok) throw await rawError(res);
    return { blob: await res.blob(), filename: res.headers.get("X-Doc-Filename") || name };
  } finally {
    clearTimeout(t);
  }
}
